import { useEffect, useState } from 'react';
import { envoisApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { oublieBoite } from '../../lib/boite-entreprise';
import { trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/**
 * Fournisseurs courants de boîtes e-mail : leur serveur d'envoi. « Autre »
 * laisse tout saisir, comme l'indique le fournisseur.
 */
export const FOURNISSEURS = [
  { id: 'infomaniak', nom: 'Infomaniak', hote: 'mail.infomaniak.com', port: 465, securite: 'SSL' },
  {
    id: 'hostpoint',
    nom: 'Hostpoint',
    hote: 'asmtp.mail.hostpoint.ch',
    port: 465,
    securite: 'SSL',
  },
  { id: 'google', nom: 'Gmail / Google', hote: 'smtp.gmail.com', port: 465, securite: 'SSL' },
  {
    id: 'microsoft',
    nom: 'Outlook / Microsoft 365',
    hote: 'smtp.office365.com',
    port: 587,
    securite: 'STARTTLS',
  },
  {
    id: 'bluewin',
    nom: 'Swisscom / Bluewin',
    hote: 'smtpauths.bluewin.ch',
    port: 465,
    securite: 'SSL',
  },
  { id: 'ovh', nom: 'OVH', hote: 'ssl0.ovh.net', port: 465, securite: 'SSL' },
  { id: 'gandi', nom: 'Gandi', hote: 'mail.gandi.net', port: 465, securite: 'SSL' },
  { id: 'ionos', nom: 'IONOS', hote: 'smtp.ionos.com', port: 465, securite: 'SSL' },
  { id: 'zoho', nom: 'Zoho', hote: 'smtp.zoho.eu', port: 465, securite: 'SSL' },
  { id: 'gmx', nom: 'GMX', hote: 'mail.gmx.net', port: 587, securite: 'STARTTLS' },
  { id: 'icloud', nom: 'iCloud', hote: 'smtp.mail.me.com', port: 587, securite: 'STARTTLS' },
  { id: 'yahoo', nom: 'Yahoo', hote: 'smtp.mail.yahoo.com', port: 465, securite: 'SSL' },
  { id: 'autre', nom: 'Autre' },
];

const VIDE = {
  adresse: '',
  nomAffiche: '',
  hote: '',
  port: 465,
  securite: 'SSL',
  utilisateur: '',
  motDePasse: '',
  copie: true,
};

/**
 * La boîte e-mail de l'entreprise : offres, factures, rappels et courriers
 * partent de son adresse (info@bektech.ch), par son propre serveur d'envoi.
 *
 * <p>Le serveur se connecte à la boîte avant de l'enregistrer : un mot de
 * passe erroné se dit tout de suite, pas au premier envoi.
 *
 * @param {object} props
 * @param {object} [props.soc] l'entreprise, pour proposer son adresse et son nom
 * @returns {JSX.Element} la carte
 */
export function BoiteEnvoiCard({ soc }) {
  const { session } = useSession();
  const peutModifier = session?.role === 'patron';
  const [boite, setBoite] = useState(null);
  const [form, setForm] = useState(null);
  const [fournisseur, setFournisseur] = useState('infomaniak');
  const [erreur, setErreur] = useState('');
  const [info, setInfo] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    let annule = false;
    Promise.resolve()
      .then(() => envoisApi.boite())
      .then((b) => {
        if (annule) return;
        setBoite(b);
        if (b?.raccordee) {
          setForm({ ...VIDE, ...b, motDePasse: '' });
          const connu = FOURNISSEURS.find((f) => f.hote === b.hote);
          setFournisseur(connu ? connu.id : 'autre');
        }
      })
      .catch(() => {
        if (!annule) setBoite({ raccordee: false });
      });
    return () => {
      annule = true;
    };
  }, []);

  const commence = () => {
    const f = FOURNISSEURS[0];
    setForm({
      ...VIDE,
      adresse: soc?.email || '',
      nomAffiche: soc?.nom || '',
      utilisateur: soc?.email || '',
      hote: f.hote,
      port: f.port,
      securite: f.securite,
    });
    setFournisseur(f.id);
    setErreur('');
    setInfo('');
    if (soc?.email) reconnais(soc.email);
  };

  const set = (patch) => {
    setErreur('');
    setInfo('');
    setForm((p) => ({ ...p, ...patch }));
  };

  // L'adresse saisie, le fournisseur se reconnaît : info@bektech.ch → Infomaniak.
  const [reconnu, setReconnu] = useState('');
  const reconnais = async (adresse) => {
    const a = (adresse || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a)) return;
    try {
      const f = await envoisApi.fournisseurBoite(a);
      if (f && f.hote) {
        const connu = FOURNISSEURS.find((x) => x.id === f.id);
        setFournisseur(connu ? connu.id : 'autre');
        setForm((p) => ({
          ...p,
          hote: f.hote,
          port: f.port,
          securite: f.securite,
          utilisateur: p.utilisateur || a,
        }));
        setReconnu(f.nom);
      } else {
        setReconnu('');
      }
    } catch {
      setReconnu('');
    }
  };

  const choisitFournisseur = (id) => {
    setFournisseur(id);
    const f = FOURNISSEURS.find((x) => x.id === id);
    if (f?.hote) set({ hote: f.hote, port: f.port, securite: f.securite });
  };

  const enregistre = async () => {
    setEnCours(true);
    setErreur('');
    setInfo('');
    try {
      const b = await envoisApi.enregistreBoite({
        adresse: (form.adresse || '').trim(),
        nomAffiche: (form.nomAffiche || '').trim(),
        hote: (form.hote || '').trim(),
        port: +form.port || 465,
        securite: form.securite,
        utilisateur: (form.utilisateur || form.adresse || '').trim(),
        motDePasse: form.motDePasse || '',
        copie: form.copie !== false,
      });
      oublieBoite();
      setBoite(b);
      setForm({ ...VIDE, ...b, motDePasse: '' });
      setInfo('✓ Boîte raccordée : vos envois partent désormais de ' + b.adresse + '.');
    } catch (e) {
      setErreur(e?.message || 'La boîte n’a pas pu être raccordée.');
    } finally {
      setEnCours(false);
    }
  };

  const essai = async () => {
    setEnCours(true);
    setErreur('');
    setInfo('');
    try {
      const r = await envoisApi.essaiBoite();
      setInfo('✓ E-mail d’essai envoyé à ' + r.adresse + ' : regardez votre boîte de réception.');
    } catch (e) {
      setErreur(e?.message || 'L’e-mail d’essai n’est pas parti.');
    } finally {
      setEnCours(false);
    }
  };

  const debranche = async () => {
    setEnCours(true);
    try {
      await envoisApi.supprimeBoite();
      oublieBoite();
      setBoite({ raccordee: false });
      setForm(null);
      setInfo('Boîte débranchée.');
    } catch (e) {
      setErreur(e?.message || 'La boîte n’a pas pu être débranchée.');
    } finally {
      setEnCours(false);
    }
  };

  const raccordee = !!boite?.raccordee;

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="✉️"
        title="Votre adresse e-mail d’envoi"
        color={C.navy}
        sub="Offres, factures, rappels et courriers partent de votre propre adresse"
      />
      <div style={{ padding: 13 }}>
        {boite === null && <div style={{ fontSize: 9, color: C.muted }}>Lecture…</div>}
        {boite && !raccordee && !form && (
          <>
            <div style={{ fontSize: 10.5, color: C.ink, lineHeight: 1.5, marginBottom: 10 }}>
              Raccordez votre boîte e-mail (par exemple info@votre-entreprise.ch) : quand vous
              envoyez une offre, une facture ou un courrier depuis SwissTech, le client le reçoit de
              votre adresse, et une copie arrive dans votre boîte.
            </div>
            {peutModifier ? (
              <Btn label="✉️ Raccorder ma boîte e-mail" color={C.navy} full onClick={commence} />
            ) : (
              <div style={{ fontSize: 9.5, color: C.muted }}>Seul le patron peut la raccorder.</div>
            )}
          </>
        )}
        {raccordee && (
          <div
            style={{
              background: C.greenL,
              border: '1px solid ' + C.green,
              borderRadius: 9,
              padding: '9px 11px',
              fontSize: 10.5,
              fontWeight: 700,
              color: C.ink,
              marginBottom: 10,
            }}
          >
            ✓ Vos envois partent de {boite.adresse}
          </div>
        )}
        {form && peutModifier && (
          <>
            <div onBlur={() => reconnais(form.adresse)}>
              <Inp
                label="Adresse e-mail"
                type="email"
                value={form.adresse}
                placeholder="info@bektech.ch"
                onChange={(e) => set({ adresse: e.target.value })}
              />
            </div>
            {reconnu && (
              <div
                role="status"
                style={{ fontSize: 10.5, color: C.green, fontWeight: 700, margin: '-4px 0 8px' }}
              >
                {trf('✓ Boîte {0} reconnue : il ne reste que le mot de passe.', reconnu)}
              </div>
            )}
            <Inp
              label="Nom affiché"
              value={form.nomAffiche || ''}
              placeholder="Nom de l’entreprise"
              onChange={(e) => set({ nomAffiche: e.target.value })}
            />
            <Inp label="Fournisseur de la boîte">
              <select
                aria-label="Fournisseur de la boîte"
                value={fournisseur}
                onChange={(e) => choisitFournisseur(e.target.value)}
                style={{
                  width: '100%',
                  border: '1px solid ' + C.border,
                  borderRadius: 10,
                  padding: '11px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  background: '#fff',
                }}
              >
                {FOURNISSEURS.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nom}
                  </option>
                ))}
              </select>
            </Inp>
            {fournisseur === 'autre' && (
              <>
                <Inp
                  label="Serveur d’envoi (SMTP)"
                  value={form.hote}
                  placeholder="mail.exemple.ch"
                  onChange={(e) => set({ hote: e.target.value })}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Inp
                      label="Port"
                      type="number"
                      value={form.port}
                      onChange={(e) => set({ port: e.target.value })}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Inp label="Sécurité">
                      <select
                        aria-label="Sécurité"
                        value={form.securite}
                        onChange={(e) => set({ securite: e.target.value })}
                        style={{
                          width: '100%',
                          border: '1px solid ' + C.border,
                          borderRadius: 10,
                          padding: '11px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          background: '#fff',
                        }}
                      >
                        <option value="SSL">SSL (465)</option>
                        <option value="STARTTLS">STARTTLS (587)</option>
                      </select>
                    </Inp>
                  </div>
                </div>
              </>
            )}
            <Inp
              label="Identifiant"
              value={form.utilisateur}
              placeholder="le plus souvent, l’adresse e-mail"
              onChange={(e) => set({ utilisateur: e.target.value })}
            />
            <Inp
              label="Mot de passe de la boîte"
              type="password"
              value={form.motDePasse}
              placeholder={raccordee ? '•••••••• (inchangé)' : ''}
              onChange={(e) => set({ motDePasse: e.target.value })}
            />
            {(fournisseur === 'google' || fournisseur === 'microsoft') && (
              <div style={{ fontSize: 9.5, color: C.muted, lineHeight: 1.45, marginBottom: 8 }}>
                Google et Microsoft demandent un « mot de passe d’application », créé dans la
                sécurité de votre compte, à la place du mot de passe habituel.
              </div>
            )}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 10.5,
                marginBottom: 10,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={form.copie !== false}
                onChange={(e) => set({ copie: e.target.checked })}
              />
              Recevoir une copie de chaque envoi dans ma boîte
            </label>
            <Btn
              label={enCours ? 'Connexion…' : raccordee ? '💾 Enregistrer' : '✉️ Raccorder'}
              color={C.navy}
              full
              onClick={enCours ? undefined : enregistre}
            />
          </>
        )}
        {raccordee && peutModifier && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <Btn
                label="📨 E-mail d’essai"
                color={C.teal}
                full
                onClick={enCours ? undefined : essai}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Btn
                label="Débrancher"
                color={C.grey}
                full
                onClick={enCours ? undefined : debranche}
              />
            </div>
          </div>
        )}
        {erreur && (
          <div role="alert" style={{ fontSize: 10.5, color: C.red, fontWeight: 700, marginTop: 8 }}>
            ✕ {erreur}
          </div>
        )}
        {info && (
          <div
            role="status"
            style={{ fontSize: 10.5, color: C.green, fontWeight: 700, marginTop: 8 }}
          >
            {info}
          </div>
        )}
      </div>
    </Card>
  );
}
